import { getCountries, type Country } from 'react-phone-number-input';

export type EnFlagVariant = 'uk' | 'us';

export type LanguageItem = {
  locale: string;
  label: string;
  code: string;
  country: string;
  flagFile: string;
};

export const LANGUAGE_ITEMS: readonly LanguageItem[] = [
  { locale: 'ar', label: 'العربية', code: 'AR', country: 'SA', flagFile: 'ar.png' },
  { locale: 'ms', label: 'Bahasa Melayu', code: 'MS', country: 'MY', flagFile: 'ms.png' },
  { locale: 'cs', label: 'Čeština', code: 'CS', country: 'CZ', flagFile: 'cs.png' },
  { locale: 'da', label: 'Dansk', code: 'DA', country: 'DK', flagFile: 'da.png' },
  { locale: 'fr', label: 'Français', code: 'FR', country: 'FR', flagFile: 'fr.png' },
  { locale: 'zh-CN', label: '简体中文', code: 'CN', country: 'CN', flagFile: 'zh-cn.png' },
  {
    locale: 'pt-BR',
    label: 'Português (Brasil)',
    code: 'BR',
    country: 'BR',
    flagFile: 'pt-br.png',
  },
  { locale: 'de', label: 'Deutsch', code: 'DE', country: 'DE', flagFile: 'de.png' },
  { locale: 'it', label: 'Italiano', code: 'IT', country: 'IT', flagFile: 'it.png' },
  { locale: 'zh-TW', label: '繁體中文', code: 'TW', country: 'TW', flagFile: 'zh-tw.png' },
  { locale: 'ru', label: 'Русский', code: 'RU', country: 'RU', flagFile: 'ru.png' },
  { locale: 'en-uk', label: 'English (UK)', code: 'EN', country: 'GB', flagFile: 'uk.png' },
  { locale: 'en-us', label: 'English (US)', code: 'EN', country: 'US', flagFile: 'us.png' },
  { locale: 'he', label: 'עברית', code: 'HE', country: 'IL', flagFile: 'he.png' },
  { locale: 'ja', label: '日本語', code: 'JA', country: 'JP', flagFile: 'ja.png' },
  { locale: 'sv', label: 'Svenska', code: 'SV', country: 'SE', flagFile: 'sw.png' },
  { locale: 'es', label: 'Español', code: 'ES', country: 'ES', flagFile: 'es.png' },
  { locale: 'no', label: 'Norsk', code: 'NO', country: 'NO', flagFile: 'no.png' },
  { locale: 'ko', label: '한국어', code: 'KO', country: 'KR', flagFile: 'ko.png' },
  { locale: 'pt-PT', label: 'Português', code: 'PT', country: 'PT', flagFile: 'pt-pt.png' },
  { locale: 'tr', label: 'Türkçe', code: 'TR', country: 'TR', flagFile: 'tr.svg' },
  { locale: 'fi', label: 'Suomi', code: 'FI', country: 'FI', flagFile: 'fi.svg' },
  { locale: 'hr', label: 'Hrvatski', code: 'HR', country: 'HR', flagFile: 'hr.svg' },
  { locale: 'el', label: 'Ελληνικά', code: 'EL', country: 'GR', flagFile: 'el.svg' },
  { locale: 'bg', label: 'Български', code: 'BG', country: 'BG', flagFile: 'bg.svg' },
  { locale: 'hi', label: 'हिन्दी', code: 'HI', country: 'IN', flagFile: 'hi.svg' },
  { locale: 'nl', label: 'Nederlands', code: 'NL', country: 'NL', flagFile: 'nl.svg' },
  { locale: 'lt', label: 'Lietuvių', code: 'LT', country: 'LT', flagFile: 'lt.svg' },
  { locale: 'sk', label: 'Slovenčina', code: 'SK', country: 'SK', flagFile: 'sk.svg' },
  { locale: 'lv', label: 'Latviešu', code: 'LV', country: 'LV', flagFile: 'lv.svg' },
  { locale: 'ka', label: 'ქართული', code: 'KA', country: 'GE', flagFile: 'ka.svg' },
  { locale: 'hu', label: 'Magyar', code: 'HU', country: 'HU', flagFile: 'hu.svg' },
  { locale: 'vi', label: 'Tiếng Việt', code: 'VI', country: 'VN', flagFile: 'vi.svg' },
  { locale: 'id', label: 'Bahasa Indonesia', code: 'ID', country: 'ID', flagFile: 'id.svg' },
  { locale: 'th', label: 'ไทย', code: 'TH', country: 'TH', flagFile: 'th.svg' },
  { locale: 'fil', label: 'Filipino', code: 'FIL', country: 'PH', flagFile: 'fil.svg' },
  { locale: 'zu', label: 'isiZulu', code: 'ZU', country: 'ZA', flagFile: 'zu.svg' },
  { locale: 'sq', label: 'Shqip', code: 'SQ', country: 'AL', flagFile: 'sq.svg' },
  { locale: 'et', label: 'Eesti', code: 'ET', country: 'EE', flagFile: 'et.svg' },
  { locale: 'pl', label: 'Polski', code: 'PL', country: 'PL', flagFile: 'pl.svg' },
];

const SUGGESTED_FALLBACK: readonly string[] = ['en-us', 'es', 'ja', 'pt-BR', 'zh-TW'];

const normalizeLocale = (lng: string) => lng.replace('_', '-').toLowerCase();
const byLocale = (locale: string) => LANGUAGE_ITEMS.find((i) => i.locale === locale) ?? null;

function getDefaultEnFlagVariant(): EnFlagVariant {
  if (typeof navigator === 'undefined') return 'us';

  const tags = [navigator.language, ...(navigator.languages || [])]
    .filter(Boolean)
    .map(normalizeLocale);

  // Tags come in the user's order of preference: the first English variant wins
  for (const t of tags) {
    if (t.startsWith('en-us')) return 'us';
    if (t.startsWith('en-gb') || t.startsWith('en-uk')) return 'uk';
  }

  // Bare "en" or another English variant: guess by timezone
  if (tags.some((t) => t === 'en' || t.startsWith('en-'))) {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      if (tz.startsWith('Europe/')) return 'uk';
    } catch {
      /* ignore */
    }
    return 'us';
  }

  return 'uk';
}

export function resolveEnFlagVariant(fromStore: EnFlagVariant | null): EnFlagVariant {
  return fromStore ?? getDefaultEnFlagVariant();
}

export const flagUrl = (flagFile: string) => `/images/welcome/flags/${flagFile}`;

// Both English items are the same translation, only the flag differs
export function localeKeyForI18nResolve(localeKey: string): string {
  return localeKey === 'en-uk' || localeKey === 'en-us' ? 'en' : localeKey;
}

function findLanguageItemForLocale(
  lang: string | undefined,
  enFlag: EnFlagVariant,
): LanguageItem | null {
  const n = normalizeLocale(lang || 'en');

  if (n === 'en' || n.startsWith('en-')) return byLocale(enFlag === 'us' ? 'en-us' : 'en-uk');

  const exact = LANGUAGE_ITEMS.find((i) => normalizeLocale(i.locale) === n);
  if (exact) return exact;

  if (n === 'pt') return byLocale('pt-PT');
  if (n === 'zh-hant' || n === 'zh-tw' || n === 'zh-hk') return byLocale('zh-TW');
  if (n === 'zh-hans' || n === 'zh-cn' || n === 'zh') return byLocale('zh-CN');

  let base = n.split('-')[0];
  if (base === 'nb' || base === 'nn') base = 'no';
  const sameBase = LANGUAGE_ITEMS.filter((i) => normalizeLocale(i.locale).split('-')[0] === base);

  return sameBase.length === 1 ? sameBase[0] : null;
}

// Countries that are not on a flag above (Mexico, Austria, Hong Kong…) resolve through
// the browser's CLDR data: und-MX -> es, und-HK -> zh-Hant
function mainLanguageOfCountry(country: string | undefined): string | undefined {
  if (!country) return undefined;
  try {
    const { language, script } = new Intl.Locale(`und-${country}`).maximize();
    return language === 'zh' ? `zh-${script}` : language;
  } catch {
    return undefined; // malformed code or a browser without Intl.Locale: no IP suggestion
  }
}

/** The item to highlight for the current i18n language; unsupported languages run in English. */
export function getActiveLanguageItem(
  i18nLang: string | undefined,
  enFlag: EnFlagVariant,
): LanguageItem {
  return (
    findLanguageItemForLocale(i18nLang, enFlag) ??
    (findLanguageItemForLocale('en', enFlag) as LanguageItem)
  );
}

export function getSuggestedLanguageItems(
  browserLang: string | undefined,
  countryByIp: string | null | undefined,
  enFlag: EnFlagVariant,
): LanguageItem[] {
  const seed: LanguageItem[] = [];
  const pushUnique = (item: LanguageItem | null) => {
    if (item && !seed.includes(item)) seed.push(item);
  };

  const browserItem = browserLang ? findLanguageItemForLocale(browserLang, enFlag) : null;
  const ipCountry = countryByIp?.toUpperCase();
  const ipItem =
    LANGUAGE_ITEMS.find((i) => i.country === ipCountry) ??
    findLanguageItemForLocale(mainLanguageOfCountry(ipCountry), enFlag);

  // Browser and IP give the same language: it takes a single row, the defaults keep all of theirs.
  const limit = browserItem && browserItem === ipItem ? 6 : 5;

  pushUnique(browserItem);
  pushUnique(ipItem);
  SUGGESTED_FALLBACK.forEach((locale) => pushUnique(byLocale(locale)));

  return seed.slice(0, limit);
}

// Non-Latin names take the place of their romanization in the alphabetical list, as in the design
const ROMANIZED: Record<string, string> = {
  ar: 'Arabiya',
  bg: 'Balgarski',
  el: 'Ellinika',
  he: 'Ivrit',
  hi: 'Hindi',
  ja: 'Nihongo',
  ka: 'Kartuli',
  ko: 'Hangugeo',
  ru: 'Russkiy',
  th: 'Thai',
  'zh-CN': 'Zhongwen jianti',
  'zh-TW': 'Zhongwen fanti',
};
const sortKey = (item: LanguageItem) => ROMANIZED[item.locale] ?? item.label;

export function getAllLanguageItems(excludeLocales: Set<string>): LanguageItem[] {
  return LANGUAGE_ITEMS.filter((item) => !excludeLocales.has(item.locale)).sort((a, b) =>
    sortKey(a).localeCompare(sortKey(b)),
  );
}

const LANGUAGE_CODES = ['en', 'en-GB', ...LANGUAGE_ITEMS.map((item) => item.locale)];

const resolveLanguageCode = (code = '') =>
  LANGUAGE_CODES.find((locale) => locale.toLowerCase() === code.toLowerCase()) || null;

const resolveCountryCode = (code = '') => {
  const COUNTRY_CODES = getCountries();

  return COUNTRY_CODES.includes(code.toUpperCase() as Country) ? code.toUpperCase() : null;
};

const parsePathPrefix = (first: string, second: string) => {
  const countryLanguage = resolveLanguageCode(second);
  const linkCountry = countryLanguage && resolveCountryCode(first);

  if (linkCountry) {
    return { language: countryLanguage, linkCountry, prefix: `/${first}/${second}` };
  }

  const language = resolveLanguageCode(first);
  return { language, linkCountry: null, prefix: language ? `/${first}` : '' };
};

export function parseOnboardingPath(pathname: string) {
  const [, first = '', second = ''] = pathname.split('/');
  const pathPrefix = parsePathPrefix(first, second);

  return {
    ...pathPrefix,
    cleanPath: pathname.slice(pathPrefix.prefix.length) || '/',
  };
}

export const buildOnboardingPath = (
  language: string,
  linkCountry: string | null,
  cleanPath: string,
) => `${linkCountry ? `/${linkCountry.toLowerCase()}` : ''}/${language}${cleanPath}`;

const LANGUAGE_PARAM = 'lang';

export const getSearchLanguage = (search: string) =>
  resolveLanguageCode(new URLSearchParams(search).get(LANGUAGE_PARAM) ?? '');

// Changes only the `lang` value; other params are kept as typed (URLSearchParams would re-encode them)
const setSearchLanguage = (search: string, language: string) => {
  const params = search
    .replace('?', '')
    .split('&')
    .map((param) =>
      param.split('=')[0] === LANGUAGE_PARAM
        ? `${LANGUAGE_PARAM}=${encodeURIComponent(language)}`
        : param,
    );

  return `?${params.join('&')}`;
};

// The same page under another link language, changed where the link has it (path or `lang` param);
// null when there is nothing to change, e.g. an old link without a language
export const getLanguageSwitchUrl = (
  pathname: string,
  search: string,
  hash: string,
  language: string,
) => {
  const { prefix, linkCountry, cleanPath } = parseOnboardingPath(pathname);
  const currentUrl = `${pathname}${search}${hash}`;
  let nextUrl = currentUrl;

  if (prefix) {
    nextUrl = `${buildOnboardingPath(language, linkCountry, cleanPath)}${search}${hash}`;
  } else if (getSearchLanguage(search)) {
    nextUrl = `${pathname}${setSearchLanguage(search, language)}${hash}`;
  }

  return nextUrl === currentUrl ? null : nextUrl;
};
